/**
 * useHomeClub Hook
 *
 * Resolves the user's home club from their enrolled clubs list.
 *
 * Under the home-club model every user has exactly one home club, which owns all
 * payment methods and is always the charging provider. Payment forms must be
 * initialized with the home club's credentials so tokens are created against the
 * correct payment account — regardless of which club's portal is currently visited.
 *
 * This mirrors the mobile app's `useHomeClub()` surface so both public apps converge
 * on the same pattern.
 *
 * @module club/hooks
 */

import { useUserClubsQuery } from './useUserClubsQuery';
import type { EnrolledClub } from '~/services/auth';

/**
 * Resolve the authenticated user's home club.
 *
 * Returns `{ homeClub }` — null for guests/unauthenticated users (the enrolled clubs
 * query is disabled) and until the `/me` clubs load.
 *
 * @example
 * ```tsx
 * const { homeClub } = useHomeClub();
 * const paymentSettings = homeClub?.settings?.paymentSettings ?? null;
 * ```
 */
export function useHomeClub(): { homeClub: EnrolledClub | null } {
  const { clubs } = useUserClubsQuery();
  const homeClub = clubs.find(c => c.isHome) ?? null;
  return { homeClub };
}
